import { Link } from "react-router-dom";
import Eyebrow from "@/components/Eyebrow";
import CountryClothWeave from "@/components/CountryClothWeave";
import PillarStrip from "@/components/PillarStrip";
import ImpactTicker from "@/components/ImpactTicker";
import FeaturedStory from "@/components/FeaturedStory";
import MaskedLines from "@/components/MaskedLines";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import { motion } from "framer-motion";

const PROGRAMS = [
  { tag: "Education", color: "#D98A18", title: "MDGCLP", body: "The Mah Doe Gibson Computer Literacy Program puts practical digital skills within reach of students and adults across District 17." },
  { tag: "Health", color: "#A62639", title: "COHEDIP", body: "Community health education and development intervention — awareness sessions, malaria sensitization, and outreach across District 17." },
  { tag: "Social Engagement", color: "#C05A3A", title: "HOFAVIPROC", body: "Our video production and media development program documents real life in rural Liberia, including the 'Life in Rural Liberia' documentary series." },
  { tag: "Research", color: "#31517E", title: "CMFRII", body: "The C. Mercy Frank Research and Innovation Initiative grounds every HOFA decision in evidence gathered with communities." },
  { tag: "Empowerment", color: "#1F5C38", title: "ESAFC Football Club", body: "Emirates Sports Association FC develops young Liberian players on and off the pitch — sport as a pathway to opportunity." },
  { tag: "Education", color: "#D98A18", title: "MAWASCF", body: "The Mary Wanbo Scholarship and Community Fund keeps promising students in school with fees, uniforms, and materials." },
];

export default function Home() {
  return (
    <>
      <SEO
        title="Five threads. One community."
        description="Hope For All Organizations weaves education, research, social engagement, economic empowerment, and health services into lasting change for Liberian families."
        path="/"
      />
      {/* HERO */}
      <section
        className="relative overflow-hidden text-white pt-[110px]"
        style={{ background: "linear-gradient(160deg, #123B23 0%, #1F5C38 78%)" }}
        data-testid="home-hero"
      >
        <div className="max-w-[1140px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-[1.15fr_.85fr] gap-14 items-center pb-[90px]">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Eyebrow tone="mango">Brewerville, Liberia · Since 2015</Eyebrow>
            </motion.div>
            <MaskedLines
              className="font-display font-bold text-white text-[clamp(42px,6vw,76px)] mb-6"
              lines={[
                <>Five threads.</>,
                <><em className="not-italic text-mango">One community.</em></>,
              ]}
              delay={0.15}
              stagger={0.14}
            />
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.75, ease: [0.19, 1, 0.22, 1] }}
              className="text-[19px] max-w-[52ch] text-white/85 mb-9"
            >
              Hope For All Organizations weaves education, research, social engagement, economic empowerment, and health services into lasting change for Liberian families — starting in Mango Town, reaching every district we serve.
            </motion.p>
            <motion.div
              className="flex gap-4 flex-wrap"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.95, ease: [0.19, 1, 0.22, 1] }}
            >
              <Link to="/programs" data-testid="hero-programs-btn" className="inline-block bg-mango text-palm-deep font-bold rounded-full px-7 py-3.5 no-underline hover:-translate-y-0.5 hover:shadow-xl transition">Explore our programs</Link>
              <Link to="/about" data-testid="hero-about-btn" className="inline-block border-2 border-white/50 text-white font-bold rounded-full px-7 py-3.5 no-underline hover:border-white transition">Our story</Link>
            </motion.div>
            <motion.div
              className="mt-11 flex gap-7 flex-wrap text-sm text-white/70"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.1 }}
            >
              <div>
                <strong className="block text-white text-[15px]">Liberia HQ</strong>
                Mango Town, Brewerville, Montserrado County — District 17
              </div>
              <div>
                <strong className="block text-white text-[15px]">USA Office</strong>
                Essex Junction, Vermont
              </div>
            </motion.div>
          </div>
          <CountryClothWeave />
        </div>
        <div className="bg-mango text-palm-deep font-bold text-[15px] py-3.5">
          <div className="max-w-[1140px] mx-auto px-6 flex gap-10 justify-between flex-wrap">
            <span>15+ active programs</span>
            <span>5 pillars — the ERSEH framework</span>
            <span>2 countries, one mission</span>
            <span>Community-led since 2015</span>
          </div>
        </div>
      </section>

      {/* IMPACT TICKER */}
      <ImpactTicker />

      {/* EDITORIAL MARQUEE — signature slow scroll */}
      <section className="py-10 bg-ink text-paper border-y border-paper/10 overflow-hidden">
        <Marquee
          speed={60}
          items={["Education", "Research", "Social Engagement", "Economic Empowerment", "Health Services"]}
          className="text-paper"
        />
      </section>

      {/* MISSION */}
      <section className="py-24">
        <div className="max-w-[1140px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-[1.2fr_.8fr] gap-14 items-start">
          <div>
            <Eyebrow>Who we are</Eyebrow>
            <h2 className="text-[clamp(30px,4vw,44px)] max-w-[24ch] mb-6">
              We lead and inspire hope for the betterment of families and communities.
            </h2>
            <p className="mb-4 max-w-[60ch] text-ink/85">HOFA is a humanitarian nonprofit founded in 2015 and headquartered in Brewerville, Montserrado County, Liberia. We believe transformation happens when communities own their development — so every HOFA program is designed, staffed, and delivered with the people it serves.</p>
            <p className="mb-4 max-w-[60ch] text-ink/85">Like Liberian country cloth, our strength comes from weaving many strips into one fabric. Each pillar of our ERSEH framework is a thread; together they hold a whole community.</p>
            <p className="max-w-[60ch] text-ink/85">We organize, support, participate, collaborate, and partner across all five pillars — from classroom literacy in Mango Town to health outreach across District 17.</p>
          </div>
          <div className="bg-white border border-ink/10 rounded-[14px] p-8">
            <h3 className="text-xl font-display mb-4">Our core values</h3>
            <ul className="list-none p-0">
              {[
                { c: "#F2A93B", t: "Teamwork" },
                { c: "#31517E", t: "Excellence" },
                { c: "#C05A3A", t: "Acceptance" },
                { c: "#1F5C38", t: "Commitment" },
                { c: "#A62639", t: "Honesty" },
              ].map((v, i, arr) => (
                <li
                  key={v.t}
                  className={`flex items-center gap-3 py-2.5 font-semibold ${i < arr.length - 1 ? "border-b border-dashed border-ink/15" : ""}`}
                >
                  <i className="block w-2.5 h-2.5 rounded-[3px]" style={{ background: v.c }} />
                  {v.t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ERSEH */}
      <section className="bg-ink text-paper py-24" id="erseh">
        <div className="max-w-[1140px] mx-auto px-6">
          <Eyebrow tone="mango">The ERSEH framework</Eyebrow>
          <h2 className="text-[clamp(30px,4vw,44px)] mb-3 text-white">Five pillars, woven together.</h2>
          <p className="text-white/70 max-w-[62ch] mb-12">
            Every HOFA program belongs to one of five pillars. Tap or hover a strip to see what each thread carries.
          </p>
          <PillarStrip />
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="py-24">
        <div className="max-w-[1140px] mx-auto px-6">
          <Eyebrow>What we do</Eyebrow>
          <h2 className="text-[clamp(30px,4vw,44px)] mb-3">Programs on the ground.</h2>
          <p className="max-w-[62ch] mb-12 text-ink/75">A selection of HOFA's 15+ active programs, each rooted in one of the five ERSEH pillars.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PROGRAMS.map((p) => (
              <div
                key={p.title}
                className="bg-white border border-ink/10 rounded-[14px] p-6 flex flex-col gap-2.5 hover:-translate-y-1 hover:shadow-xl transition"
                data-testid={`home-program-${p.title.toLowerCase().replace(/\s/g, "-")}`}
              >
                <span
                  className="self-start text-[11.5px] font-bold tracking-[0.1em] uppercase px-2.5 py-1 rounded-full text-white"
                  style={{ background: p.color }}
                >
                  {p.tag}
                </span>
                <h3 className="text-lg font-display">{p.title}</h3>
                <p className="text-[15px] text-ink/75">{p.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/programs" className="inline-block border-2 border-palm text-palm rounded-full px-6 py-2.5 font-bold no-underline hover:bg-palm hover:text-white transition" data-testid="home-see-all-programs">
              See all programs →
            </Link>
          </div>
        </div>
      </section>

      {/* ESAFC */}
      <section className="text-white py-24" style={{ background: "linear-gradient(150deg,#31517E,#1E3557)" }}>
        <div className="max-w-[1140px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <Eyebrow tone="mango">New in 2026</Eyebrow>
            <h2 className="text-[clamp(30px,4vw,44px)] mb-5 text-white">Football with a purpose: ESAFC joins the HOFA family.</h2>
            <p className="text-white/85 mb-4">HOFA has acquired Emirates Sports Association FC, a Liberian third-division club — because a football pitch can be a classroom, a clinic, and a career path all at once.</p>
            <p className="text-white/85 mb-6">Through a 12-month player development plan, structured pre-season training, and the Football Knowledge Development Program (FKDP 101), ESAFC develops the whole player: skills, education, and character.</p>
            <Link to="/esafc" className="inline-block bg-mango text-palm-deep font-bold rounded-full px-7 py-3.5 no-underline hover:-translate-y-0.5 hover:shadow-xl transition" data-testid="home-esafc-btn">
              Meet the club
            </Link>
          </div>
          <div
            className="aspect-[4/3] rounded-[14px] relative overflow-hidden border-[3px] border-white/35"
            style={{ background: "linear-gradient(180deg,#2E7D4F,#1F5C38)" }}
            aria-hidden="true"
          >
            <div className="absolute left-0 right-0 top-1/2 h-0.5 bg-white/35" />
            <div className="absolute top-1/2 left-1/2 w-28 h-28 border-2 border-white/35 rounded-full -translate-x-1/2 -translate-y-1/2" />
            <b className="absolute bottom-5 left-0 right-0 text-center font-display text-2xl tracking-[0.06em]">ESAFC</b>
          </div>
        </div>
      </section>

      {/* FEATURED STORY */}
      <FeaturedStory />

      {/* GET INVOLVED */}
      <section className="py-24">
        <div className="max-w-[1140px] mx-auto px-6">
          <Eyebrow>Join the work</Eyebrow>
          <h2 className="text-[clamp(30px,4vw,44px)] mb-12">Every thread strengthens the cloth.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { bg: "#1F5C38", title: "Donate", body: "Your gift funds classrooms, health outreach, and youth programs directly in Liberian communities.", cta: "Give today →", to: "/get-involved#donate" },
              { bg: "#D98A18", title: "Volunteer", body: "Lend your skills — in Liberia, in Vermont, or remotely. Teachers, health workers, and storytellers all welcome.", cta: "Raise your hand →", to: "/get-involved#volunteer" },
              { bg: "#C05A3A", title: "Partner", body: "Organizations, churches, and institutions: collaborate with HOFA on programs across all five pillars.", cta: "Start a conversation →", to: "/get-involved#partner" },
            ].map((c) => (
              <Link
                key={c.title}
                to={c.to}
                data-testid={`home-inv-${c.title.toLowerCase()}`}
                className="rounded-[14px] p-8 text-white no-underline flex flex-col gap-3 hover:-translate-y-1 transition"
                style={{ background: c.bg }}
              >
                <h3 className="text-2xl font-display">{c.title}</h3>
                <p className="text-white/90 flex-1">{c.body}</p>
                <span className="font-bold border-b-2 border-white/60 self-start pb-0.5 hover:border-white">{c.cta}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
